/**
 * Общие единицы и идентификаторы. Всё, что считает engine, — целые числа (детерминизм клиента и сервера, MADR 0002):
 *
 * - **время** — тики симуляции, {@link TICK_RATE} в секунду; в секундах ничего не храним;
 * - **длины и координаты** — фиксированная точка, {@link FIXED_ONE} единиц = одна клетка поля;
 * - **доли и проценты** — промилле (‰), {@link PERMILLE} = 100 %;
 * - **монеты** — целые, без дробей.
 *
 * Система координат поля (мир, не экран): `x` — поперёк поля слева направо (центр 0), `y` — вдоль поля от `0` до
 * `field.depth`. Штаб стоит в центре поля (`x = 0`, `y = field.depth / 2`), платформы героев — вокруг него, зомби
 * появляются на краях поля и идут к центру. Изометрию и сортировку по глубине делает рендер.
 */
import { z } from "zod";

/** Частота шага симуляции, тиков в секунду. */
export const TICK_RATE = 30;
/** Длительность тика в миллисекундах (для цикла рендера; engine о миллисекундах не знает). */
export const TICK_MS = 1000 / TICK_RATE;
/** Единиц фиксированной точки в одной клетке поля. */
export const FIXED_ONE = 1000;
/** 100 % в промилле. */
export const PERMILLE = 1000;

/** Где открыта игра. */
export const Platform = z.enum(["telegram_ios", "telegram_android", "telegram_desktop", "telegram_web", "browser"]);
export type Platform = z.infer<typeof Platform>;

/** Целое число (любой знак). */
export const Int = z.number().int();
/** Целое ≥ 0. */
export const UInt = z.number().int().nonnegative();
/** Целое > 0. */
export const PosInt = z.number().int().positive();
/** Номер тика от начала волны (0 — первый тик волны). */
export const Tick = UInt;
/** Длительность в тиках. */
export const Ticks = UInt;
/** Доля в промилле, 0…1000. */
export const Permille = z.number().int().min(0).max(PERMILLE);
/** Множитель или прибавка в промилле без верхней границы (например, +250 ‰ урона, 1500 ‰ = ×1,5). */
export const PermilleAny = z.number().int();
/** Монеты. */
export const Coins = UInt;
/** Потолок кошелька: монеты — `number`, и сумма с любой наградой должна оставаться точной (ниже 2⁵³ ≈ 9·10¹⁵). */
export const COINS_MAX = 1e15;
/** Сид ГПСЧ волны: беззнаковое 32-битное целое. Выдаёт сервер (см. `NextWave` в api/player). */
export const Seed = z
  .number()
  .int()
  .min(0)
  .max(2 ** 32 - 1);
/** Время на сервере, мс с эпохи Unix (только в HTTP API, не в бою). */
export const UnixMs = UInt;

/** Короткий идентификатор контента: `lower_snake_case`, 2–40 символов (`boost_crit_chance`, `boss_butcher`). */
export const ContentId = z.string().regex(/^[a-z][a-z0-9_]{1,39}$/, "content id: lower_snake_case");

/** Идентификатор сущности внутри одного боя (зомби, снаряд, эффект). Уникален в пределах волны, не переиспользуется. */
export const EntityId = UInt;

/** Точка на поле в единицах фиксированной точки ({@link FIXED_ONE} = клетка). */
export const Vec2 = z.object({ x: Int, y: Int });
export type Vec2 = z.infer<typeof Vec2>;

/**
 * Направление взгляда спрайта (8 направлений сетки, в мировых координатах): `n` — вдоль +y (к зомби),
 * `s` — к штабу, `e` — +x. Рендер выбирает анимацию `…/<anim>/<dir>` из манифеста атласа.
 */
export const Direction = z.enum(["n", "ne", "e", "se", "s", "sw", "w", "nw"]);
export type Direction = z.infer<typeof Direction>;

/**
 * Пять героев v1 (грилл 2026-10-24; открываются на волнах 1/5/10/20/30 — точные числа в данных баланса) и четыре премиум-героя
 * беты (`radiation`, `cryo`, `laser`, `plague`): в данных обычного режима их нет.
 */
export const HeroId = z.enum(["gunner", "bomber", "sparky", "blaze", "frosty", "radiation", "cryo", "laser", "plague"]);
export type HeroId = z.infer<typeof HeroId>;

/** Четыре вида обычных зомби v1: обычный, быстрый, толстый, со щитом. */
export const ZombieKind = z.enum(["walker", "runner", "brute", "shield"]);
export type ZombieKind = z.infer<typeof ZombieKind>;

/** Номер платформы героя: 0…15 (обычный режим — шесть платформ, бета — до 16, сетка 4×4 вокруг штаба). */
export const PlatformSlot = z.number().int().min(0).max(15);
export type PlatformSlot = z.infer<typeof PlatformSlot>;
/** Сколько платформ на поле. */
export const PLATFORM_COUNT = 6;
/** Предел платформ беты (ветка Tech `base`). */
export const MAX_PLATFORM_COUNT = 16;

/** Язык интерфейса: EN на корне, RU на `/ru`, UK на `/uk`. */
export const Locale = z.enum(["en", "ru", "uk"]);
export type Locale = z.infer<typeof Locale>;

/**
 * Код языка Telegram или браузера (`uk`, `ru-RU`, `en_US`) → локаль игры; неизвестный — `null`. Одно правило на клиент,
 * бота и счета: остальные языки получают английский (`?? "en"`).
 */
export function localeForLanguage(code: string | null | undefined): Locale | null {
  const base = (code ?? "").trim().toLowerCase().split(/[-_]/)[0];
  return base === "en" || base === "ru" || base === "uk" ? base : null;
}

/** Ключ строки i18n (`hero.gunner.name`); тексты живут в клиенте (C2), в данных — только ключи. */
export const I18nKey = z.string().regex(/^[a-zA-Z0-9_.-]+$/);
